import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../config/api';
import './ReviewModal.css';

const ReviewModal = ({ order, onClose, onReviewSubmitted }) => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [deliveryHoverRating, setDeliveryHoverRating] = useState(0);
  const [deliveryComment, setDeliveryComment] = useState('');
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [restaurantHoverRating, setRestaurantHoverRating] = useState(0);
  const [restaurantComment, setRestaurantComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSkip = () => {
    onClose();
  };

  const handleSubmit = async () => {
    // Only submit if at least one rating is provided
    if (deliveryRating === 0 && restaurantRating === 0) {
      showError(t('review.errorAtLeastOne'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/reviews`, {
        orderId: order._id,
        deliveryRating: deliveryRating > 0 ? deliveryRating : null,
        deliveryComment: deliveryComment.trim(),
        restaurantRating: restaurantRating > 0 ? restaurantRating : null,
        restaurantComment: restaurantComment.trim()
      });

      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      showError(error.response?.data?.message || t('review.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, hoverRating, setRating, setHoverRating, disabled = false) => {
    const displayRating = hoverRating || rating;
    return (
      <div 
        className="star-rating"
        onMouseLeave={() => !disabled && setHoverRating(0)}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= displayRating ? 'filled' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && setRating(star)}
            onMouseEnter={() => !disabled && setHoverRating(star)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="review-modal-header">
          <h2>{t('review.title')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="review-modal-content">
          {/* Delivery rating - first */}
          <div className="review-step">
            <h3>{t('review.deliveryTitle')}</h3>
            <p className="step-description">{t('review.deliveryDescription')}</p>
            {renderStars(deliveryRating, deliveryHoverRating, setDeliveryRating, setDeliveryHoverRating)}
            <textarea
              className="review-comment"
              placeholder={t('review.deliveryCommentPlaceholder')}
              value={deliveryComment}
              onChange={(e) => setDeliveryComment(e.target.value)}
              rows={3}
            />
          </div>

          {/* Restaurant/Store rating - second */}
          <div className="review-step">
            <h3>{order.restaurantType === 'Store' ? t('review.storeTitle') : t('review.restaurantTitle')}</h3>
            <p className="step-description">{order.restaurantType === 'Store' ? t('review.storeDescription') : t('review.restaurantDescription')}</p>
            {renderStars(restaurantRating, restaurantHoverRating, setRestaurantRating, setRestaurantHoverRating)}
            <textarea
              className="review-comment"
              placeholder={order.restaurantType === 'Store' ? t('review.storeCommentPlaceholder') : t('review.restaurantCommentPlaceholder')}
              value={restaurantComment}
              onChange={(e) => setRestaurantComment(e.target.value)}
              rows={3}
            />
          </div>

          <div className="review-actions">
            <button className="btn btn-secondary" onClick={handleSkip}>
              {t('review.skip')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || (deliveryRating === 0 && restaurantRating === 0)}
            >
              {submitting ? t('review.submitting') : t('review.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
